import {
    addAudioLogEntry,
    errorMessageIsInteractBeforePlay,
    getPreferredRingtoneVolume,
} from 'flow/utils/browser-utils';
import { MainAudioContext } from './audio/wrapped-audio-context';
import RingtoneCalm from 'ringtones/Calm.mp3';

const RINGTONE_VOLUME_MULTIPLIER = 0.5;
const DEFAULT_VOLUME = 1.0;

const handlePlayBeforeInteract = (error) => {
    if (errorMessageIsInteractBeforePlay(error)) {
        addAudioLogEntry(
            `Unable to play ringtone because no interaction was performed. Aborting. ${error}`,
        );
        console.warn(error);
    } else {
        throw error;
    }
};

export default class RingTonePlayerClass {
    constructor(volume = DEFAULT_VOLUME) {
        const { context } = MainAudioContext;

        const gain = context.createGain();
        gain.connect(MainAudioContext.getPrimaryOutput().getNode());
        gain.connect(MainAudioContext.getSecondaryOutput().getNode());

        const audioElement = new Audio();
        audioElement.autoplay = false;
        audioElement.loop = true;
        audioElement.preload = true;
        audioElement.crossOrigin = 'use-credentials';

        const audioElementSource =
            context.createMediaElementSource(audioElement);
        audioElementSource.connect(gain);

        this.audioElement = audioElement;
        this.gain = gain;
        this.playPromiseResolved = false;
        this.context = context;
        this.setVolume(volume);
    }

    log = (logEntry) => {
        addAudioLogEntry(`RingTonePlayerClass: ${logEntry}`);
    };

    setVolume = (volume) => {
        this.gain.gain.setValueAtTime(
            RINGTONE_VOLUME_MULTIPLIER * volume,
            this.context.currentTime,
        );
    };

    setUrl = async (url) => {
        if (!url) {
            this.log(
                'Attempted to set an empty URL as ringtone source. Aborting.',
            );
            return;
        }

        const promise = new Promise((resolve, reject) => {
            const onCanplay = () => {
                removeEventListeners();
                resolve();
            };
            const onError = (error) => {
                removeEventListeners();
                reject(error);
            };
            const removeEventListeners = () => {
                this.audioElement.removeEventListener('canplay', onCanplay);
                this.audioElement.removeEventListener('error', onError);
            };
            this.audioElement.addEventListener('canplay', onCanplay);
            this.audioElement.addEventListener('error', onError);
        });

        try {
            this.audioElement.src = url;
            await promise;
        } catch (_error) {
            this.log('Failed to load audio source, using default');
            this.audioElement.src = RingtoneCalm;
        }
    };

    play = async () => {
        this.playPromiseResolved = false;
        try {
            await MainAudioContext.context.resume();
            this.log('Attempting to play ringtone.');
            const { audioElement } = this;
            if (audioElement.src === null) {
                this.log(
                    'Attempted to play ringtone with no source set. Aborting.',
                );
                return;
            }

            this.log(`Playback rate before play: ${audioElement.playbackRate}`);
            MainAudioContext.getPrimaryOutput().play();
            MainAudioContext.getSecondaryOutput().play();

            audioElement.currentTime = 0;

            // Make sure the audio is not already playing
            if (audioElement.paused) {
                await audioElement.play();
            }

            this.log(`Playback rate after play: ${audioElement.playbackRate}`);
        } catch (error) {
            handlePlayBeforeInteract(error);
        } finally {
            this.playPromiseResolved = true;
        }
    };

    stop = async () => {
        this.log('Stopping ringtone.');
        if (!this.playPromiseResolved) {
            // We want to prevent a situation where we attempt to stop the ringtone before it has started playing
            // If we don't, we might end up in situations where the ringtone is never stopped
            await this.waitForPlay(1500);
        }
        this.audioElement.pause();
        MainAudioContext.getPrimaryOutput().pause();
        MainAudioContext.getSecondaryOutput().pause();
        MainAudioContext.context.suspend();
    };

    waitForPlay = async (timeout) => {
        const start = Date.now();

        while (!this.playPromiseResolved && Date.now() - start < timeout) {
            await new Promise((resolve) => setTimeout(resolve, 100));
        }
    };
}

export const RingTonePlayer = new RingTonePlayerClass(
    getPreferredRingtoneVolume(),
);
